import { Import, Lock } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import type { FileRejection } from 'react-dropzone';

import React, { useCallback, useState } from 'react';

import { resolutionsFromDto, unknownLabels } from '@splitfiesta/core';
import type { ImportFileError, SplitwiseFile } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Alert } from '@splitfiesta/ui';

import { useResolveSplitwiseLabelsMutation } from '@/hooks/queries/useImports';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { readImportFile } from '@/lib/importFile';

import { CsvGuide } from './ui/CsvGuide';
import { ImportOption } from './ui/ImportOption';
import { InstructionSteps } from './ui/InstructionSteps';
import { SPLITWISE_SOURCE } from './ui/importSources';

interface ImportFileStepProps {
  onParsed: (file: SplitwiseFile, fileName: string) => void;
  /** Name of the file already parsed, when the user steps back to this step. */
  fileName?: string | null;
}

export function ImportFileStep({ onParsed, fileName }: ImportFileStepProps) {
  const { t } = useTranslation();
  useNamespace('import');
  const { isOnline } = useNetworkStatus();
  const [error, setError] = useState<ImportFileError | null>(null);
  const [reading, setReading] = useState(false);
  const { mutateAsync: resolveLabels } = useResolveSplitwiseLabelsMutation();
  const source = SPLITWISE_SOURCE;

  /**
   * Labels the English table does not know go to the server once; the import
   * never depends on the answer, so any failure (offline, timeout, an older
   * API without the endpoint) keeps the first parse and its heuristics.
   */
  const withResolvedLabels = useCallback(
    async (picked: File, file: SplitwiseFile): Promise<SplitwiseFile> => {
      const labels = unknownLabels(file);
      if (labels.length === 0) return file;
      try {
        const dto = await resolveLabels({ labels });
        const reparsed = await readImportFile(picked, resolutionsFromDto(dto));
        return 'file' in reparsed ? reparsed.file : file;
      } catch {
        return file;
      }
    },
    [resolveLabels]
  );

  const handleDrop = useCallback(
    async (accepted: File[], rejected: FileRejection[]) => {
      // Take a file rejected for its type too: our own validator is the one
      // with a message that says why, and a silent no-op reads as a broken
      // picker. Other rejections (a multi-file drop) are not a single choice,
      // so importing the first of them is not what the user asked for.
      const picked =
        accepted[0] ??
        rejected.find(r => r.errors.some(e => e.code === 'file-invalid-type'))
          ?.file;
      if (!picked) return;

      setError(null);
      setReading(true);
      try {
        const result = await readImportFile(picked);
        if ('error' in result) setError(result.error);
        else
          onParsed(await withResolvedLabels(picked, result.file), picked.name);
      } finally {
        setReading(false);
      }
    },
    [onParsed, withResolvedLabels]
  );

  const disabled = !isOnline || reading;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    multiple: false,
    disabled,
  });

  let pickLabel = t('import:file.choose');
  if (reading) pickLabel = t('import:file.parsing');
  else if (fileName) pickLabel = t('import:file.replace');

  return (
    <div className="space-y-4">
      <div className="space-y-8">
        <CsvGuide />
        <ImportOption title={t(source.titleKey)}>
          <InstructionSteps
            testId="import-instructions"
            steps={source.stepKeys.map(key => t(key))}
          />
        </ImportOption>
      </div>
      <p className="flex items-start justify-center gap-2 px-4 text-center text-xs leading-4 text-gray-500 dark:text-gray-400">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{t('import:file.privacy')}</span>
      </p>

      {!isOnline && (
        <Alert type="warning" message={t('import:errors.offline')} />
      )}
      {error && <Alert type="error" message={t(error.key, error.params)} />}

      <div
        {...getRootProps({
          role: 'button',
          'aria-label': pickLabel,
          'aria-disabled': disabled,
        })}
        data-testid="import-dropzone"
        className={`flex flex-col items-center gap-2 rounded-xl border-2 border-dashed p-10 text-center transition-colors ${
          isDragActive
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600'
        } ${disabled ? 'opacity-50' : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-500'}`}
      >
        <input
          {...getInputProps()}
          data-testid="import-file-input"
          disabled={disabled}
        />
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-500/10">
          <Import className="h-7 w-7 text-primary-500" aria-hidden="true" />
        </span>
        <span className="font-medium text-gray-900 dark:text-white">
          {pickLabel}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('import:file.dropHint')}
        </span>
        {fileName && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {fileName}
          </span>
        )}
      </div>

      {/* Nominative use of a third-party mark: name it only to describe the
          import, and say so, so nothing reads as a partnership. */}
      <p
        className="text-center text-xs text-gray-400 dark:text-gray-500"
        data-testid="import-trademark-notice"
      >
        {t(source.trademarkKey)}
      </p>
    </div>
  );
}
